import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { mapProgramCurriculumsForCollectionDisplay } from 'bundles/collections-carousel/utils/mappingUtils';
import type { CollectionType } from 'bundles/enterprise-collections/components/CollectionItem';
import EnterpriseCollectionsContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseCollectionsContext';
import { useCuratedCollections } from 'bundles/page-config-common/contexts/enterprise/EnterpriseCuratedCollectionsContext';
import { useCurriculumClips } from 'bundles/page-config-common/contexts/enterprise/EnterpriseCurriculumClipsContext';
import { useForCreditCollection } from 'bundles/page-config-common/contexts/enterprise/EnterpriseForCreditCollectionContext';
import { useSpecializationSessions } from 'bundles/page-config-common/contexts/enterprise/EnterpriseSpecializationSessionsContext';
import { useCampusWordingForLearner } from 'bundles/unified-common/hooks/useCampusWordingForLearner';

type Props = {
  children: React.ReactNode;
};

const EnterpriseCollectionsProvider = ({ children }: Props) => {
  const { programSlug } = useParams();

  const { forCreditCollection } = useForCreditCollection();
  const { curatedCollections } = useCuratedCollections();
  const { s12nSessions, s12nSessionsLoading } = useSpecializationSessions();
  const { clips, clipsLoading } = useCurriculumClips();
  const isCampusWording = useCampusWordingForLearner();

  if (s12nSessionsLoading || clipsLoading) {
    return (
      <EnterpriseCollectionsContext.Provider
        value={{ isEnterpriseCollectionsLoading: true, enterpriseCollections: undefined }}
      >
        {children}
      </EnterpriseCollectionsContext.Provider>
    );
  }

  const programCurriculumCollections = [forCreditCollection, ...(curatedCollections || [])].filter(
    Boolean
  ) as Array<CollectionType>;

  return (
    <EnterpriseCollectionsContext.Provider
      value={{
        isEnterpriseCollectionsLoading: false,
        enterpriseCollections: mapProgramCurriculumsForCollectionDisplay(
          programSlug,
          programCurriculumCollections,
          s12nSessions,
          clips,
          isCampusWording
        ).map((collection) => {
          return {
            ...collection,
            // For private products that only display if a user is part of/invited to the program.
            // They show up in this list, but only have ID present. Check for presence of slug and name to ensure entities are valid.
            entities: collection?.entities?.filter((entity) => !!entity?.slug && !!entity?.name),
          };
        }),
      }}
    >
      {children}
    </EnterpriseCollectionsContext.Provider>
  );
};

export default EnterpriseCollectionsProvider;
